// Settings → Platform → Notifications. Every email Conduit sends, grouped by
// area: who receives it, whether it is switched on, and an editor for the copy.
import { useEffect, useState } from "react";
import { Mail, Loader2, Pencil, AlertTriangle, PencilLine } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { EmailTemplateEditor } from "@/pages/settings/EmailTemplateEditor";

export function EmailTemplatesCard({ canEdit = false }) {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [busyId, setBusyId] = useState("");

  const load = () => api.get("/settings/email-templates")
    .then((r) => setData(r.data))
    .catch(() => setData({ items: [] }));
  useEffect(() => { load(); }, []);

  if (!data) {
    return <Card className="flex h-28 items-center justify-center border-slate-200 shadow-sm">
      <Loader2 className="h-4 w-4 animate-spin text-slate-300" />
    </Card>;
  }

  const replace = (row) => setData((d) => ({ ...d, items: d.items.map((t) => (t.id === row.id ? row : t)) }));

  const toggle = async (t, enabled) => {
    setBusyId(t.id);
    try {
      const { data: row } = await api.put(`/settings/email-templates/${t.id}`, { enabled });
      replace(row);
      toast[enabled ? "success" : "warning"](
        enabled ? `“${row.label}” is on` : `“${row.label}” is off — nothing will be sent`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update");
    } finally { setBusyId(""); }
  };

  const groups = data.groups || [];
  const off = data.items.filter((t) => !t.enabled).length;

  return (
    <Card className="border-slate-200 p-6 shadow-sm" data-testid="settings-email-templates-card">
      <div className="flex flex-wrap items-center gap-2">
        <Mail className="h-4 w-4 text-primary" />
        <h3 className="font-heading text-base font-semibold text-slate-800 dark:text-slate-100">Email templates</h3>
        <span className="text-[11px] text-slate-400">{data.items.length} templates</span>
        {off > 0 && (
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:bg-amber-500/15 dark:text-amber-300"
            data-testid="email-templates-off-count">{off} switched off</span>
        )}
      </div>
      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
        The copy for every email the platform sends. Edit the wording, drop in placeholders, preview it and
        send yourself a test. Templates can&rsquo;t be deleted — each one is wired to a feature — but any of
        them can be switched off.
      </p>

      {data.paused && (
        <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200"
          data-testid="email-templates-paused-warning">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          Outbound email is paused platform-wide, so a test send only arrives if the address is on the QA
          allowlist (Settings → Platform → Outbound email).
        </div>
      )}

      {groups.map((g) => (
        <div key={g} className="mt-5" data-testid={`email-template-group-${g.toLowerCase()}`}>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{g}</div>
          <ul className="mt-1 divide-y divide-slate-100 dark:divide-slate-800">
            {data.items.filter((t) => t.group === g).map((t) => (
              <li key={t.id} className="flex items-start gap-3 py-2.5" data-testid={`email-template-row-${t.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-slate-800 dark:text-slate-100">{t.label}</span>
                    {t.customized?.length > 0 && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary"
                        data-testid={`email-template-edited-${t.id}`}>
                        <PencilLine className="h-2.5 w-2.5" /> edited
                      </span>
                    )}
                    {!t.enabled && (
                      <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-700 dark:bg-amber-500/15 dark:text-amber-300"
                        data-testid={`email-template-off-${t.id}`}>off</span>
                    )}
                  </div>
                  <div className="truncate text-xs text-slate-500 dark:text-slate-400">{t.subject}</div>
                  <div className="mt-0.5 text-[11px] text-slate-400">{t.audience}</div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {busyId === t.id
                    ? <Loader2 className="h-4 w-4 animate-spin text-slate-300" />
                    : <Switch checked={t.enabled} disabled={!canEdit}
                        onCheckedChange={(v) => toggle(t, v)}
                        data-testid={`email-template-enabled-${t.id}`} />}
                  <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs"
                    onClick={() => setEditing(t)} data-testid={`email-template-edit-${t.id}`}>
                    <Pencil className="h-3 w-3" /> Edit
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ))}

      {editing && (
        <EmailTemplateEditor template={editing} canEdit={canEdit} myEmail={data.my_email}
          syntaxHelp={data.syntax_help || []}
          onClose={() => setEditing(null)}
          onSaved={(row) => { replace(row); setEditing((e) => (e && e.id === row.id ? row : e)); }} />
      )}
    </Card>
  );
}

export default EmailTemplatesCard;
